import React, {useState} from 'react'
import {Box, Typography} from '@mui/material'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import withLayout from '../../hocs/Layout'
import ForwardDelayed from './Forward'
import useForwardDelay from '../../common/hooks/useForwardDelay'
import useRtoDelay from '../../common/hooks/useRtoDelay'
import usePickupDelay from '../../common/hooks/usePickupDelay'
import Loader from '../../common/Loader'
import {getMerchant} from '../../helpers/ReduxHelpers'
import PageTitle from '../../common/PageTitle'
import PickupDelay from './PickupDelay'
import RtoDelay from './RtoDelay'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE, SUCCESS, ERROR, WARNING, RED} from '../../styles/foundation/colors'
import {LiveSnapshotNote} from '../../common/DashboardDateFilter'
import {SHADOW} from '../../styles/foundation/effects'
import {pageContainerSx} from '../../styles/patterns'
import {segTrackSx, segItemSx} from '../dashboard/dashboardStyles'

const statCardSx = {
  bgcolor: WHITE, borderRadius: '10px', p: '16px', textAlign: 'center',
  boxShadow: SHADOW.card, border: `1px solid ${BORDER}`,
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {transform: 'translateY(-2px)', boxShadow: SHADOW.cardHover},
}
const sectionSx = {
  bgcolor: WHITE, borderRadius: '10px', boxShadow: SHADOW.card,
  border: `1px solid ${BORDER}`, overflow: 'hidden', mb: 2.5,
}

// One alerts block for a given freight type (B2C = D2C, B2B = PTL).
function AlertsBlock({shipmentType}) {
  const merchant = getMerchant()
  const merchantId = merchant.id ? merchant.id : ''
  const [activeSection, setActiveSection] = useState('forward')

  const {isCalculating, data: forwardDelayed} = useForwardDelay(merchantId, shipmentType)
  const {isCalculating: isCalculatingRTO, data: rtoDelayed} = useRtoDelay(merchantId, shipmentType)
  const {isCalculating: isCalculatingPickupDelay, pickUpDelayed} = usePickupDelay(merchantId, shipmentType)

  const loading = isCalculating || isCalculatingPickupDelay || isCalculatingRTO

  const fwdData = forwardDelayed || []
  const pkData = pickUpDelayed || []
  const rtoData = rtoDelayed || []
  const totalAlerts = fwdData.length + pkData.length + rtoData.length

  const sections = [
    {key: 'forward', title: 'Forward Delivery Delays', sub: 'Shipments in transit beyond expected delivery time', icon: <LocalShippingOutlinedIcon />, color: ERROR, count: fwdData.length},
    {key: 'pickup', title: 'Pickup Delays', sub: 'Shipments awaiting pickup beyond scheduled time', icon: <ScheduleOutlinedIcon />, color: WARNING, count: pkData.length},
    {key: 'rto', title: 'RTO Delays', sub: 'Return shipments delayed in transit back to origin', icon: <ReplayOutlinedIcon />, color: RED, count: rtoData.length},
  ]

  return (
    <Box>
      {/* Live-snapshot note — Shipment Alerts is "as of now", not date-filtered (delay endpoints take no date range) */}
      <LiveSnapshotNote
        sx={{mb: 2}}
        title='Current delays, updated live'
        message='Total Alerts = shipments overdue right now across forward delivery, pickup and RTO (breach ageing > 0). This is a live view and is not filtered by a date range.'
      />

      {loading ? <Loader /> : (
        <>
          {/* KPI cards — cockpit metric strip (matches the Shipments tab). Click a delay
              metric to jump to its section below. */}
          <Box sx={{
            display: 'grid', gridTemplateColumns: {xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)'},
            bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden',
            boxShadow: SHADOW.card, mb: '20px',
          }}>
            {sections.map((s) => (
              <Box key={s.key} onClick={() => setActiveSection(s.key)}
                sx={{p: 1.75, borderRight: `1px solid ${BORDER}`, cursor: 'pointer', '&:hover': {bgcolor: '#fafbfc'}}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                  <Box sx={{width: 7, height: 7, borderRadius: '2px', bgcolor: s.color, flex: 'none'}} />
                  <Typography sx={{fontSize: '9.5px', fontWeight: WEIGHT.extra, letterSpacing: '0.05em', textTransform: 'uppercase', color: SUBTLE, fontFamily: FONT}}>{s.title}</Typography>
                </Box>
                <Typography sx={{fontSize: '22px', fontWeight: WEIGHT.extra, lineHeight: 1, color: s.color, fontFamily: FONT, fontVariantNumeric: 'tabular-nums', mt: 0.75}}>{s.count}</Typography>
              </Box>
            ))}
            <Box sx={{p: 1.75}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                <WarningAmberOutlinedIcon sx={{fontSize: 13, color: NAVY}} />
                <Typography sx={{fontSize: '9.5px', fontWeight: WEIGHT.extra, letterSpacing: '0.05em', textTransform: 'uppercase', color: SUBTLE, fontFamily: FONT}}>Total Alerts</Typography>
              </Box>
              <Typography sx={{fontSize: '22px', fontWeight: WEIGHT.extra, lineHeight: 1, color: NAVY, fontFamily: FONT, fontVariantNumeric: 'tabular-nums', mt: 0.75}}>{totalAlerts}</Typography>
            </Box>
          </Box>

          {totalAlerts === 0 && (
            <Box sx={{...statCardSx, textAlign: 'center', py: 5}}>
              <CheckCircleOutlineIcon sx={{fontSize: 40, color: SUCCESS, mb: 1}} />
              <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY}}>No delayed shipments</Typography>
              <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.5}}>
                All forward, pickup and RTO shipments are within expected timelines.
              </Typography>
            </Box>
          )}

          {/* Delay sections — tabbed (Forward / Pickup / RTO) so users switch instantly
              instead of expanding each accordion. */}
          {totalAlerts > 0 && (() => {
            const active = sections.find((s) => s.key === activeSection) || sections[0]
            return (
              <Box sx={sectionSx}>
                {/* Tab strip */}
                <Box sx={{display: 'flex', borderBottom: `1px solid ${BORDER}`, overflowX: 'auto'}}>
                  {sections.map((s) => {
                    const on = active.key === s.key
                    return (
                      <Box key={s.key} onClick={() => setActiveSection(s.key)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1.5, cursor: 'pointer',
                          whiteSpace: 'nowrap', borderBottom: `2px solid ${on ? s.color : 'transparent'}`,
                          bgcolor: on ? '#fafbfc' : 'transparent', transition: 'all 0.15s',
                          '&:hover': {bgcolor: '#fafbfc'},
                        }}>
                        <Box sx={{color: on ? s.color : SUBTLE, display: 'flex', '& svg': {fontSize: 18}}}>{s.icon}</Box>
                        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: on ? NAVY : SUBTLE}}>
                          {s.title}
                        </Typography>
                        <Box sx={{
                          fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.bold, px: 0.85, py: 0.1, borderRadius: '6px',
                          color: on ? s.color : SUBTLE, bgcolor: on ? s.color + '1f' : '#eef1f5',
                        }}>{s.count}</Box>
                      </Box>
                    )
                  })}
                </Box>
                {/* Active panel */}
                <Box sx={{p: {xs: 1.5, md: 2.5}}}>
                  <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mb: 2}}>{active.sub}</Typography>
                  {active.key === 'forward' && <ForwardDelayed data={fwdData} />}
                  {active.key === 'pickup' && <PickupDelay pickUpDelayed={pkData} />}
                  {active.key === 'rto' && <RtoDelay rtoDelayed={rtoData} />}
                </Box>
              </Box>
            )
          })()}
        </>
      )}
    </Box>
  )
}

function ControlTower({shipmentType: forced}) {
  const [vertical, setVertical] = useState('B2C')
  // Embedded inside the Dashboard (vertical comes from the route/Common tab):
  // render just the alerts — the Dashboard supplies the page wrapper, title & hero.
  if (forced) {
    return <AlertsBlock shipmentType={forced} />
  }
  // Standalone (/control-tower): keep the page wrapper + its own D2C/PTL toggle.
  return (
    <Box sx={pageContainerSx}>
      <PageTitle>Shipment Alerts</PageTitle>
      <Box sx={{...segTrackSx, mb: 2}}>
        {[{v: 'B2C', l: 'D2C'}, {v: 'B2B', l: 'PTL'}].map((o) => (
          <Box key={o.v} onClick={() => setVertical(o.v)} sx={segItemSx(vertical === o.v)}>{o.l}</Box>
        ))}
      </Box>
      <AlertsBlock shipmentType={vertical} />
    </Box>
  )
}

export {ControlTower as ShipmentAlerts}
export default withLayout(ControlTower)
